/**
 * Deletes a client member and invalidates the members cache.
 *
 * @param clientId - Client ID used only for cache invalidation, not for org scoping
 *
 * @returns Mutation result with `{ mutate, mutateAsync, isPending, isError, isSuccess }`
 *
 * @remarks
 * - Org scoping is handled automatically via the X-Tenant-Id header injected by httpClient
 * - clientId is used solely to invalidate `CLIENT_MEMBERS_KEYS.byClient(orgId, clientId)` on success
 *
 * @example
 * const deleteMember = useDeleteClientMember('client-1');
 * await deleteMember.mutateAsync('cm-123');
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { clientMembersService } from '../api/clientMembers.service';
import { CLIENT_MEMBERS_KEYS } from './queryKeys';

export function useDeleteClientMember(clientId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;

  return useMutation({
    mutationFn: (memberId: string) => clientMembersService.remove(clientId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_MEMBERS_KEYS.byClient(orgId, clientId) });
    },
  });
}
